import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      if (response.data.user.is_admin) {
        localStorage.setItem('admin_token', response.data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        toast.success('Welcome back!');
        navigate('/admin/dashboard');
      } else {
        setError('This account does not have admin privileges');
      }
    } catch (error) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-ivory flex flex-col items-center justify-center p-4"
      data-testid="admin-login-page"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232E2A26' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md relative">
        {/* Logo Area */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block group">
            <h1 className="font-heading text-3xl font-light text-charcoal tracking-wide">
              JAT FASHION
            </h1>
            <p className="text-[10px] tracking-[0.25em] uppercase text-gold font-medium mt-1">
              House™
            </p>
          </Link>
        </div>

        {/* Card */}
        <div 
          className="bg-white border border-charcoal/10 shadow-xl p-8 md:p-10 animate-[fade-in-up_0.5s_ease_forwards]"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-gold" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl font-light text-charcoal" data-testid="login-title">
              Admin Portal
            </h2>
            <p className="text-sm text-charcoal-light mt-2">
              Sign in to manage your store
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start gap-3" data-testid="login-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" strokeWidth={1.5} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-12 pl-12 pr-4 bg-beige/30 border border-charcoal/10 text-charcoal placeholder-charcoal-light/60 
                    focus:border-gold focus:ring-1 focus:ring-gold focus:bg-white outline-none transition-all"
                  required
                  data-testid="input-email"
                  placeholder="admin@jatfashion.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" strokeWidth={1.5} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-12 pl-12 pr-12 bg-beige/30 border border-charcoal/10 text-charcoal placeholder-charcoal-light/60 
                    focus:border-gold focus:ring-1 focus:ring-gold focus:bg-white outline-none transition-all"
                  required
                  data-testid="input-password"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal-light hover:text-charcoal transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gold text-white font-medium text-sm tracking-wider uppercase 
                hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed 
                flex items-center justify-center gap-2 transition-all group mt-2"
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-8 pt-6 border-t border-charcoal/10">
            <p className="text-xs text-center text-charcoal-light mb-3">Demo Credentials</p>
            <div className="bg-beige/50 p-4 text-center">
              <p className="text-sm text-charcoal">
                <span className="text-charcoal-light">Email:</span> admin@jatfashion.com
              </p>
              <p className="text-sm text-charcoal mt-1">
                <span className="text-charcoal-light">Password:</span> admin123
              </p>
            </div>
          </div>
        </div>

        {/* Back to Store Link */}
        <div className="text-center mt-6">
          <Link 
            to="/" 
            className="text-sm text-charcoal-light hover:text-gold transition-colors inline-flex items-center gap-1"
          >
            ← Back to Store
          </Link>
        </div>
      </div>

      {/* Footer */}
      <p className="text-xs text-charcoal-light/60 mt-8">
        © {new Date().getFullYear()} JAT Fashion House™. All rights reserved.
      </p>
    </div>
  );
};

export default AdminLogin;
